import { useEffect, useState } from "react";
import {
  Settings,
  Database,
  User2Icon,
  MapPin,
  Building2,
  Ruler,
  Warehouse,
  Tags,
  Package,
  UserPlus,
  Percent,
  ShoppingBag,
  Coins,
  FileText,
  ClipboardList,
  CreditCard,
  Banknote,
  CalendarDays,
  Factory,
  Truck,
  CheckCircle,
  PackageCheck,
  HeartHandshake,
  UserCog,
  KeyRound,
  LogOut,
  Layers,
} from "lucide-react";

import { getRoleId } from "Common/Components/getRoleId";
import { canViewSection, SectionKey } from "Common/constants/roles";
import useMenuCounts from "Common/hooks/useMenuCounts";

// یک آیتم منو که به یک «بخش» گره خورده است.
type WorkflowMenuItem = {
  section: SectionKey;
  id: string;
  label: string;
  link: string;
  icon: JSX.Element;
};

// آیتم‌هایی که در سطح اول منو می‌مانند، بلافاصله بعد از «داده‌های اولیه».
const TOP_LEVEL_MENU_ITEMS: WorkflowMenuItem[] = [
    {
      section: "allOrders",
      id: "all-orders",
      label: "همه سفارش‌ها",
      link: "/AllOrders",
      icon: <Layers />,
    },
    // «سایر سفارش‌ها» دقیقاً زیر «همه سفارش‌ها» می‌نشیند؛ همان فهرست است ولی
    // فقط‌خواندنی و بدون محدودشدن به رکوردهای خودِ کارشناس فروش.
    {
      section: "otherOrders",
      id: "other-orders",
      label: "سایر سفارش‌ها",
      link: "/OtherOrders",
      icon: <Layers />,
    },
    {
      section: "proformaInvoices",
      id: "proforma-invoices",
      label: "پیش‌فاکتورها",
      link: "/ProformaInvoices",
      icon: <FileText />,
    },
    {
      section: "pendingPayments",
      id: "pending-payments",
      label: "سفارش‌های در انتظار تأیید واریزی",
      link: "/PendingPayments",
      icon: <CalendarDays />,
    },
    {
      section: "confirmedPayments",
      id: "confirmed-payments",
      label: "سفارش‌های تأیید واریزی شده",
      link: "/ConfirmedPayments",
      icon: <CheckCircle />,
    },
  ];

// زیرمجموعه‌های «سفارش‌ها» — گردش وضعیت خودِ سفارش.
const ORDER_MENU_ITEMS: WorkflowMenuItem[] = [
    {
      section: "salesOrders",
      id: "sales-orders",
      label: "سفارش‌های فروش",
      link: "/SalesOrders",
      icon: <ClipboardList />,
    },
    {
      section: "ordersInProduction",
      id: "orders-in-production",
      label: "سفارش‌های در حال تولید",
      link: "/OrdersInProduction",
      icon: <Factory />,
    },
    {
      section: "producedOrders",
      id: "produced-orders",
      label: "سفارش‌های تولید شده",
      link: "/ProducedOrders",
      icon: <PackageCheck />,
    },
    {
      section: "ordersInWarehouse",
      id: "orders-in-warehouse",
      label: "سفارش‌های در انبار",
      link: "/OrdersInWarehouse",
      icon: <Warehouse />,
    },
    {
      section: "ordersExitedWarehouse",
      id: "orders-exited-warehouse",
      label: "سفارش‌های خارج شده از انبار",
      link: "/OrdersExitedWarehouse",
      icon: <Truck />,
    },
    {
      section: "loadedOrders",
      id: "loaded-orders",
      label: "سفارش‌های بارگیری شده",
      link: "/LoadedOrders",
      icon: <Package />,
    },
    {
      section: "deliveredOrders",
      id: "delivered-orders",
      label: "سفارش‌های تحویل شده به مشتری",
      link: "/DeliveredOrders",
      icon: <HeartHandshake />,
    },
  ];

// زیرمجموعه‌های «کالاهای سفارش‌ها» — گردش وضعیت قلم‌های کالا.
const GOODS_MENU_ITEMS: WorkflowMenuItem[] = [
    {
      section: "readyToProduceGoods",
      id: "ready-to-produce-goods",
      label: "کالاهای آماده تولید",
      link: "/ReadyToProduceGoods",
      icon: <CalendarDays />,
    },
    {
      section: "goodsInProduction",
      id: "goods-in-production",
      label: "کالاهای در حال تولید",
      link: "/GoodsInProduction",
      icon: <Factory />,
    },
    {
      section: "producedGoods",
      id: "produced-goods",
      label: "کالاهای تولید شده",
      link: "/ProducedGoods",
      icon: <CheckCircle />,
    },
    {
      section: "productsWarehouse",
      id: "products-warehouse",
      label: "کالاهای در انبار محصولات",
      link: "/ProductsWarehouse",
      icon: <Warehouse />,
    },
    {
      section: "goodsExitedWarehouse",
      id: "goods-exited-warehouse",
      label: "کالاهای خارج شده از انبار",
      link: "/GoodsExitedWarehouse",
      icon: <Truck />,
    },
    {
      section: "loadedGoods",
      id: "loaded-goods",
      label: "کالاهای بارگیری شده",
      link: "/LoadedGoods",
      icon: <Package />,
    },
    {
      section: "deliveredGoods",
      id: "delivered-goods",
      label: "کالاهای تحویل شده به مشتری",
      link: "/DeliveredGoods",
      icon: <HeartHandshake />,
    },
  ];

/** آیتم‌های قابل‌نمایش برای این نقش، بدون فیلد section که فقط برای فیلترکردن بود. */
const visibleItems = (items: WorkflowMenuItem[], userRole: any) =>
  items
    .filter((item) => canViewSection(userRole, item.section))
    .map(({ section, ...item }) => item);

/**
 * یک گروه منو با زیرمجموعه. اگر هیچ زیرمجموعه‌ای برای این نقش قابل‌نمایش نباشد،
 * آرایه‌ی خالی برمی‌گردد تا گروه اصلاً ساخته نشود.
 */
const group = (
  id: string,
  label: string,
  icon: JSX.Element,
  items: WorkflowMenuItem[],
  userRole: any
) => {
  const subItems = visibleItems(items, userRole);
  return subItems.length > 0 ? [{ id, label, link: "/#", icon, subItems }] : [];
};

/** ارقام لاتین به فارسی، تا عدد داخل پرانتز با بقیه‌ی رابط هم‌خوان باشد. */
const faDigits = (value: number): string =>
  String(value).replace(/[0-9]/g, (d) => "۰۱۲۳۴۵۶۷۸۹"[Number(d)]);

/** همه‌ی مسیرهای یک درخت منو (برای اینکه بدانیم چه چیزهایی باید شمرده شوند). */
const collectLinks = (items: any[]): string[] =>
  items.flatMap((item) => [
    ...(item?.link && item.link !== "/#" ? [item.link] : []),
    ...(Array.isArray(item?.subItems) ? collectLinks(item.subItems) : []),
  ]);

/**
 * عدد تعداد رکورد را به عنوان هر آیتم می‌چسباند: «پیش‌فاکتورها (۳۵)».
 * آیتمی که شمارشش در دسترس نیست (بدون دسترسی یا بدون منبع) بدون عدد می‌ماند.
 */
const withCounts = (items: any[], counts: Record<string, number>): any[] =>
  items.map((item) => {
    const count = item?.link ? counts[item.link] : undefined;
    return {
      ...item,
      label:
        typeof count === "number" ? `${item.label} (${faDigits(count)})` : item.label,
      ...(Array.isArray(item?.subItems)
        ? { subItems: withCounts(item.subItems, counts) }
        : {}),
    };
  });

const MenuData = () => {
  const [userRole, setUserRole] = useState<any>(null);

  useEffect(() => {
    const role = getRoleId(
      localStorage.getItem("app_at_mahan") || ""
    );

    setUserRole(role);
  }, []);

  const menuData: any = [
    ...(canViewSection(userRole, "initialData")
      ? [
        {
          id: "initialData",
          label: "داده‌های اولیه",
          link: "/#",
          icon: <Database />,
          subItems: [
            {
              id: "provincial-registration",
              label: "ثبت استان",
              link: "/ProvincialRegistration",
              icon: <MapPin />,
            },
            {
              id: "city-registration",
              label: "ثبت شهرستان",
              link: "/CityRegiste",
              icon: <Building2 />,
            },
            {
              id: "measurement-registration",
              label: "ثبت واحد اندازه‌گیری",
              link: "/MeasurementPage",
              icon: <Ruler />,
            },
            {
              id: "warehouse-registration",
              label: "ثبت انبار",
              link: "/WarehouseRegistration",
              icon: <Warehouse />,
            },
            {
              id: "goods-type-registration",
              label: "ثبت نوع کالا",
              link: "/GoodsTypeRegistration",
              icon: <Tags />,
            },
            {
              id: "goods-and-services-registration",
              label: "ثبت کالا و خدمت",
              link: "/GoodsAndServicesRegistration",
              icon: <Package />,
            },
            {
              id: "customer-registration",
              label: "ثبت مشتری",
              link: "/CustomerRegistration",
              icon: <UserPlus />,
            },
            // «حداقل پرداختی» زیرمجموعه‌ی داده‌های اولیه است ولی دسترسی جداگانه دارد:
            // کارشناس فروش و مدیر فروش آن را نمی‌بینند.
            ...(canViewSection(userRole, "minimumPayment")
              ? [
                {
                  id: "minimum-payment-settings",
                  label: "حداقل پرداختی",
                  link: "/MinimumPaymentSettings",
                  icon: <Percent />,
                },
              ]
              : []),
            {
              id: "product-registration",
              label: "ثبت محصول",
              link: "/ProductRegistration",
              icon: <ShoppingBag />,
            },
            {
              id: "product-price-registration",
              label: "ثبت قیمت محصول",
              link: "/ProductPriceRegistration",
              icon: <Coins />,
            },
            {
              id: "currency-registration",
              label: "ثبت ارز",
              link: "/CurrencyRegistration",
              icon: <Banknote />,
            },
            {
              id: "payment-type-registration",
              label: "ثبت نوع پرداخت",
              link: "/PaymentTypeRegistration",
              icon: <CreditCard />,
            },
            {
              id: "proforma-descriptions",
              label: "ثبت توضیحات پیش‌فاکتور",
              link: "/ProformaDescriptions",
              icon: <FileText />,
            },
            ...(canViewSection(userRole, "salesAvailability")
              ? [{
                id: "sales-availability",
                label: "فعال/غیرفعال کردن فروش",
                link: "/SalesAvailability",
                icon: <CheckCircle />,
              }]
              : []),
            {
              id: "contact-registration",
              label: "ثبت رابط",
              link: "/ContactRegistration",
              icon: <HeartHandshake />,
            },
            // «تعیین جایگزین» هم زیرمجموعه‌ی داده‌های اولیه است ولی مثل «حداقل پرداختی»
            // دسترسی جداگانه دارد: فقط ادمین و سوپرادمین آن را می‌بینند.
            ...(canViewSection(userRole, "salesSubstitutions")
              ? [
                {
                  id: "sales-substitutions",
                  label: "تعیین جایگزین",
                  link: "/SalesSubstitutions",
                  icon: <UserCog />,
                },
              ]
              : []),
          ],
        },
      ]
      : []),

    // هر آیتم به یک «بخش» گره خورده و فقط برای نقش‌هایی که آن بخش را دارند نمایش
    // داده می‌شود. مرجع نگاشت نقش↔بخش: Common/constants/roles.ts
    ...visibleItems(TOP_LEVEL_MENU_ITEMS, userRole),

    // «سفارش‌ها» و «کالاهای سفارش‌ها» فقط وقتی ساخته می‌شوند که کاربر دست‌کم یکی از
    // زیرمجموعه‌هایشان را ببیند، وگرنه یک گروهِ خالی در منو می‌ماند.
    ...group("orders-group", "سفارش‌ها", <ShoppingBag />, ORDER_MENU_ITEMS, userRole),
    ...group("goods-group", "کالاهای سفارش‌ها", <Package />, GOODS_MENU_ITEMS, userRole),

    // بخش «فاکتور فروش» غیرفعال شد. (مسیر /SalesInvoice هم صفحه‌ای نداشت.)


    ...(canViewSection(userRole, "userManagement")
      ? [
        {
          id: "usersManagement",
          label: "کاربران",
          link: "/#",
          icon: <User2Icon />,
          subItems: [
            {
              id: "manage-users",
              label: "مدیریت کاربران",
              link: "/manageUser",
              icon: <UserCog />,
            },
          ],
        },
      ]
      : []),

    {
      id: "settings",
      label: "تنظیمات",
      link: "/#",
      icon: <Settings />,
      subItems: [
        {
          id: "change-password",
          label: "تغییر رمز عبور",
          link: "/user-profile",
          icon: <KeyRound />,
        },
        {
          id: "logout",
          label: "خروج",
          link: "/logout",
          icon: <LogOut />,
        },
      ],
    },
  ];

  // شمارش‌ها فقط برای آیتم‌هایی گرفته می‌شوند که همین کاربر در منو می‌بیند.
  const counts = useMenuCounts(collectLinks(menuData));

  return withCounts(menuData, counts);
};

export default MenuData;
